import { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Download, RefreshCw, Pencil, Eye, CheckCircle2, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import OnboardingDraftEditor, { Draft } from './OnboardingDraftEditor';
import PdfPreviewModal from './PdfPreviewModal';
import { changeText, SourceChange } from './OnboardingDocumentStatus';

// Verbale di onboarding (backend routes/onboarding.ts + services/onboarding/document.ts,
// 2026-10-30): alla fine del wizard si completano i dati che il wizard non raccoglie (cliente,
// numero di contratto, modalità di gestione, report, data di attivazione) e si scrive una bozza sul
// modello del partner del tenant. La bozza si rilegge, si modifica e si vede in anteprima, come le
// considerazioni dei report; solo "Make final" crea una revisione numerata, immutabile, scaricabile.

interface Profile {
  contractCode?: string; attentionOf?: string; autoPatchCritical?: boolean | null;
  managementMode?: 'business_first' | 'security_first' | 'none' | null; honeypot?: boolean | null;
  socMissingAction?: string; edrMissingAction?: string; reportFrequency?: string; reportEmails?: string[];
  socActivationDate?: string; notes?: string;
}
interface Company { legal_name?: string; address?: string; vat_number?: string; contact_name?: string; contact_phone?: string }
interface Doc { id: number; revision: number; file_name: string; sha256: string | null; created_at: string; created_by_name: string | null; changes?: SourceChange[] | null }

const DEFAULTS: Profile = {
  socMissingAction: 'Segnalare negli incontri e nei report mensili',
  edrMissingAction: 'Si autorizza sin d’ora l’installazione',
  reportFrequency: 'mensile',
};

function YesNo({ value, onChange, disabled }: { value: boolean | null | undefined; onChange: (v: boolean) => void; disabled: boolean }) {
  return (
    <div className="flex gap-3 text-sm">
      <label className="inline-flex items-center gap-1"><input type="radio" disabled={disabled} checked={value === true} onChange={() => onChange(true)} /> Sì</label>
      <label className="inline-flex items-center gap-1"><input type="radio" disabled={disabled} checked={value === false} onChange={() => onChange(false)} /> No</label>
    </div>
  );
}

export default function OnboardingDocumentPanel({ tenantId, isAdmin }: { tenantId: number; isAdmin: boolean }) {
  const { user } = useAuth();
  // L'anagrafica del cliente la modifica solo chi è di livello partner o platform (backend).
  const canEditCompany = user?.scopeLevel !== 'tenant';
  const [available, setAvailable] = useState<boolean | null>(null);
  const [partnerName, setPartnerName] = useState('');
  const [profile, setProfile] = useState<Profile>({});
  const [company, setCompany] = useState<Company>({});
  const [emails, setEmails] = useState('');
  const [docs, setDocs] = useState<Doc[]>([]);
  const [canManageDraft, setCanManageDraft] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null);
  const [busy, setBusy] = useState<'save' | 'draft' | 'preview' | 'final' | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  async function load() {
    const res = await axios.get(`/api/onboarding/${tenantId}/document`);
    const d = res.data.data;
    setAvailable(d.template.available);
    setPartnerName(d.template.partnerName || '');
    const p: Profile = { ...DEFAULTS, ...d.profile };
    setProfile(p);
    setEmails((p.reportEmails || []).join(', '));
    setCompany(d.company || {});
    setDocs(d.documents || []);
    setCanManageDraft(!!d.canManageDraft);
    setDraft(d.hasDraft ? (await axios.get(`/api/onboarding/${tenantId}/document/draft`)).data.data : null);
  }
  useEffect(() => { load().catch(() => setAvailable(false)); }, [tenantId]);

  const set = (patch: Partial<Profile>) => setProfile(p => ({ ...p, ...patch }));
  const setC = (patch: Partial<Company>) => setCompany(c => ({ ...c, ...patch }));

  async function save(): Promise<boolean> {
    setBusy('save'); setMessage(null);
    try {
      const reportEmails = emails.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean);
      await axios.put(`/api/onboarding/${tenantId}/document-profile`, { profile: { ...profile, reportEmails }, company });
      setMessage({ ok: true, text: 'Document data saved.' });
      // I dati salvati possono far risultare la bozza non più allineata.
      if (draft) setDraft((await axios.get(`/api/onboarding/${tenantId}/document/draft`)).data.data);
      return true;
    } catch (err: any) {
      setMessage({ ok: false, text: err.response?.data?.error || 'Could not save the document data' });
      return false;
    } finally { setBusy(null); }
  }

  // Crea la bozza o la riallinea a modello e dati salvati (le sezioni modificate a mano restano).
  async function writeDraft() {
    if (!(await save())) return;
    setBusy('draft'); setMessage(null);
    try {
      const res = await axios.post(`/api/onboarding/${tenantId}/document/draft`, {});
      const kept = res.data.data.sections.filter((s: { edited: boolean }) => s.edited).length;
      setDraft(res.data.data);
      setMessage({ ok: true, text: draft
        ? `Draft updated from the wizard data${kept ? `; ${kept} section(s) edited by hand were kept` : ''}.`
        : `Draft written${kept ? `; ${kept} section(s) edited in the previous revision were carried over` : ''}. Review it, then make it final.` });
    } catch (err: any) {
      setMessage({ ok: false, text: err.response?.data?.error || 'Could not write the draft' });
    } finally { setBusy(null); }
  }

  async function showPreview() {
    setBusy('preview'); setMessage(null);
    try {
      const res = await axios.get(`/api/onboarding/${tenantId}/document/draft/pdf`, { responseType: 'blob' });
      setPreview({ url: URL.createObjectURL(res.data), title: `Draft preview, Rev.${draft?.revision ?? ''}` });
    } catch (err: any) {
      setMessage({ ok: false, text: err.response?.status === 429 ? 'Too many previews, retry in a few minutes.' : 'Could not generate the preview' });
    } finally { setBusy(null); }
  }

  async function discardDraft() {
    if (!window.confirm('Discard this draft and every change made to it?')) return;
    await axios.delete(`/api/onboarding/${tenantId}/document/draft`);
    setDraft(null);
    setMessage({ ok: true, text: 'Draft discarded.' });
  }

  async function finalize() {
    if (!draft) return;
    setBusy('final'); setMessage(null);
    try {
      const res = await axios.post(`/api/onboarding/${tenantId}/document/draft/finalize`, { version: draft.version, acceptOutdated: draft.outdated });
      setFinalizing(false); setEditing(false);
      setMessage({ ok: true, text: `Rev.${res.data.data.revision} is final: ${res.data.data.file_name}.` });
      await load();
      download(res.data.data);
    } catch (err: any) {
      setFinalizing(false);
      setMessage({ ok: false, text: err.response?.data?.error || 'Could not finalize the document' });
      await load().catch(() => undefined);
    } finally { setBusy(null); }
  }

  async function viewRevision(doc: Doc) {
    setMessage(null);
    try {
      const res = await axios.get(`/api/onboarding/${tenantId}/documents/${doc.id}/download`, { responseType: 'blob' });
      setPreview({ url: URL.createObjectURL(res.data), title: `Rev.${doc.revision}, ${doc.file_name}` });
    } catch {
      setMessage({ ok: false, text: 'Could not open this revision' });
    }
  }

  async function download(doc: Doc) {
    const res = await axios.get(`/api/onboarding/${tenantId}/documents/${doc.id}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = doc.file_name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  if (available === null) return null;
  if (!available) {
    return (
      <div className="rounded-md border border-gray-100 p-3 text-sm text-gray-500 inline-flex items-center gap-2">
        <FileText size={14} /> No onboarding document template is configured for {partnerName || 'the partner of this tenant'}.
      </div>
    );
  }

  const ro = !isAdmin;
  const field = 'input-field w-full text-sm';
  return (
    <div className="rounded-md border border-primary-100 bg-primary-50/30 p-4 space-y-4">
      <div>
        <h3 className="font-medium text-gray-900 inline-flex items-center gap-2"><FileText size={15} className="text-primary-600" /> Onboarding document (verbale di onboarding) — template {partnerName}</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          The document takes contacts, notification levels, critical assets, night-time and remote access from the previous steps.
          Complete here what the wizard does not ask; empty fields are printed highlighted as "da definire". Then write a draft, review it and make it final.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-gray-500 uppercase">End customer (cover page)</h4>
          <input disabled={ro || !canEditCompany} className={field} placeholder="Company name (ragione sociale)" value={company.legal_name || ''} onChange={e => setC({ legal_name: e.target.value })} />
          <textarea disabled={ro || !canEditCompany} className={field} rows={2} placeholder="Address (street, postcode, city - country)" value={company.address || ''} onChange={e => setC({ address: e.target.value })} />
          <input disabled={ro || !canEditCompany} className={field} placeholder="VAT number (P.IVA)" value={company.vat_number || ''} onChange={e => setC({ vat_number: e.target.value })} />
          {!canEditCompany && <p className="text-[11px] text-gray-400">Company details are maintained by your service provider.</p>}
          <input disabled={ro} className={field} placeholder={`For the attention of (default from template)`} value={profile.attentionOf || ''} onChange={e => set({ attentionOf: e.target.value })} />
        </div>
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-gray-500 uppercase">Contract and service</h4>
          <input disabled={ro || !canEditCompany} className={field} placeholder="Contract code" value={profile.contractCode || ''} onChange={e => set({ contractCode: e.target.value })} />
          <label className="block text-xs text-gray-500">SOC activation date
            <input disabled={ro || !canEditCompany} type="date" className={field} value={profile.socActivationDate || ''} onChange={e => set({ socActivationDate: e.target.value })} />
          </label>
          <label className="block text-xs text-gray-500">Report frequency
            <select disabled={ro} className={field} value={profile.reportFrequency || ''} onChange={e => set({ reportFrequency: e.target.value })}>
              {['settimanale', 'mensile', 'trimestrale', 'semestrale'].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <input disabled={ro} className={field} placeholder="Report recipients (customer emails, comma separated)" value={emails} onChange={e => setEmails(e.target.value)} />
        </div>
      </div>

      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-gray-500 uppercase">General management of the system (section 5)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div><span className="text-xs text-gray-500 block">Auto-patching includes critical assets?</span><YesNo disabled={ro} value={profile.autoPatchCritical} onChange={v => set({ autoPatchCritical: v })} /></div>
          <div><span className="text-xs text-gray-500 block">Mode</span>
            <select disabled={ro} className={field} value={profile.managementMode || ''} onChange={e => set({ managementMode: (e.target.value || null) as Profile['managementMode'] })}>
              <option value="">—</option><option value="business_first">Business first</option><option value="security_first">Security first</option><option value="none">None</option>
            </select>
          </div>
          <div><span className="text-xs text-gray-500 block">Honeypot enabled?</span><YesNo disabled={ro} value={profile.honeypot} onChange={v => set({ honeypot: v })} /></div>
        </div>
        <label className="block text-xs text-gray-500">SOC not installed on new assets, what to do?
          <input disabled={ro} className={field} value={profile.socMissingAction || ''} onChange={e => set({ socMissingAction: e.target.value })} />
        </label>
        <label className="block text-xs text-gray-500">EDR not installed, what to do?
          <input disabled={ro} className={field} value={profile.edrMissingAction || ''} onChange={e => set({ edrMissingAction: e.target.value })} />
        </label>
        <label className="block text-xs text-gray-500">Notes (section 1)
          <input disabled={ro} className={field} value={profile.notes || ''} onChange={e => set({ notes: e.target.value })} />
        </label>
      </div>

      {message && <p className={`text-sm ${message.ok ? 'text-green-700' : 'text-red-700'}`}>{message.text}</p>}
      {isAdmin && (
        <div className="flex flex-wrap gap-2">
          <button onClick={save} disabled={busy !== null} className="btn-secondary">{busy === 'save' ? 'Saving…' : 'Save document data'}</button>
          {canManageDraft && (
            <button onClick={writeDraft} disabled={busy !== null} className={draft ? 'btn-secondary inline-flex items-center gap-1' : 'btn-primary inline-flex items-center gap-1'}>
              {busy === 'draft' ? <><RefreshCw size={13} className="animate-spin" /> Writing…</> : draft ? <><RefreshCw size={13} /> Update draft from data</> : <><FileText size={13} /> Write draft</>}
            </button>
          )}
        </div>
      )}
      {!canManageDraft && <p className="text-xs text-gray-500">The document is reviewed and issued by your service provider; final revisions appear below.</p>}

      {draft && canManageDraft && (
        <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 space-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="font-medium text-gray-900">Draft of Rev.{draft.revision}</span>
            <span className="text-xs text-gray-500">{new Date(draft.updatedAt).toLocaleString('en-GB')}{draft.updatedByName ? ` · ${draft.updatedByName}` : ''}</span>
            <span className="text-xs text-gray-600">{draft.sections.filter(s => s.edited).length} section(s) edited by hand</span>
            {draft.staleEdited.length > 0 && <span className="text-xs text-amber-800">{draft.staleEdited.length} of them do not include newer wizard data</span>}
            <span className={`text-xs ${draft.missingFields ? 'text-amber-800' : 'text-green-700'}`}>{draft.missingFields ? `${draft.missingFields} field(s) still to define` : 'No field left to define'}</span>
          </div>
          {draft.outdated && (
            <p className="text-xs text-amber-800 inline-flex items-center gap-1"><AlertTriangle size={12} /> The partner template or the wizard data changed after this draft was written. Use "Update draft from data" to bring it in line.</p>
          )}
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setEditing(true)} disabled={busy !== null} className="btn-secondary inline-flex items-center gap-1"><Pencil size={13} /> Edit contents</button>
              <button onClick={showPreview} disabled={busy !== null} className="btn-secondary inline-flex items-center gap-1">
                {busy === 'preview' ? <><RefreshCw size={13} className="animate-spin" /> Generating…</> : <><Eye size={13} /> Preview</>}
              </button>
              <button onClick={() => setFinalizing(true)} disabled={busy !== null} className="btn-primary inline-flex items-center gap-1"><CheckCircle2 size={13} /> Make final (Rev.{draft.revision})</button>
              <button onClick={discardDraft} disabled={busy !== null} className="text-sm text-red-700 hover:underline inline-flex items-center gap-1 px-2"><Trash2 size={13} /> Discard</button>
            </div>
          )}
        </div>
      )}

      {editing && draft && (
        <OnboardingDraftEditor tenantId={tenantId} draft={draft} onClose={() => setEditing(false)} onChanged={setDraft} onPreview={showPreview} onFinalize={() => setFinalizing(true)} />
      )}
      {preview && <PdfPreviewModal url={preview.url} title={preview.title} onClose={() => setPreview(null)} />}
      {finalizing && draft && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Make the document final">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3">
            <h3 className="font-medium text-gray-900">Make Rev.{draft.revision} final?</h3>
            <p className="text-sm text-gray-600">The document gets today's date and revision number and can no longer be changed. Any later change needs a new revision.</p>
            {draft.missingFields > 0 && <p className="text-sm text-amber-800 inline-flex items-start gap-1"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {draft.missingFields} field(s) are still highlighted as "da definire" and will be printed that way.</p>}
            {draft.staleEdited.length > 0 && <p className="text-sm text-amber-800 inline-flex items-start gap-1"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> These sections were edited by hand and do not include newer wizard data: {draft.sections.filter(s => draft.staleEdited.includes(s.key)).map(s => s.title).join(', ')}.</p>}
            {draft.outdated && <p className="text-sm text-amber-800 inline-flex items-start gap-1"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> The template or the wizard data changed after this draft was written: the document will not include those changes.</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setFinalizing(false)} disabled={busy !== null} className="btn-secondary">Keep editing</button>
              <button onClick={finalize} disabled={busy !== null} className="btn-primary">{busy === 'final' ? 'Generating…' : 'Make final'}</button>
            </div>
          </div>
        </div>
      )}

      {docs.length > 0 && (
        <table className="w-full text-sm bg-white border border-gray-100 rounded">
          <thead><tr><th className="table-header">Revision</th><th className="table-header">File</th><th className="table-header">Generated</th><th className="table-header">Changes from the previous</th><th className="table-header" /></tr></thead>
          <tbody>
            {docs.map((d, i) => (
              <tr key={d.id} className="border-t border-gray-100">
                <td className="table-cell whitespace-nowrap">Rev.{d.revision}{i === 0 ? <span className="block text-[10px] text-green-700">latest</span> : <span className="block text-[10px] text-gray-400">superseded</span>}</td>
                <td className="table-cell font-mono text-xs">{d.file_name}{d.sha256 && <span className="block text-[10px] text-gray-400" title="SHA-256 of the generated PDF">sha256 {d.sha256.slice(0, 16)}…</span>}</td>
                <td className="table-cell text-gray-500">{new Date(d.created_at).toLocaleString('en-GB')}{d.created_by_name ? ` · ${d.created_by_name}` : ''}</td>
                <td className="table-cell text-xs text-gray-600">{d.changes?.length ? <ul className="space-y-0.5">{d.changes.map(c => <li key={c.area}>{changeText(c)}</li>)}</ul> : d.changes ? 'None' : '—'}</td>
                <td className="table-cell whitespace-nowrap">
                  <button onClick={() => viewRevision(d)} className="text-primary-600 hover:underline inline-flex items-center gap-1 mr-3"><Eye size={13} /> View</button>
                  <button onClick={() => download(d)} className="text-primary-600 hover:underline inline-flex items-center gap-1"><Download size={13} /> PDF</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
